import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, RefreshCw, Trash2, Upload } from 'lucide-react';
import {
  financeService,
  type ReconcileBatchDecisions,
  type ReconcileBatchSummary,
  type ReconcilePreview,
  type ReconcilePreviewRow,
  type ReconcileAllocation,
} from '@/services/financeService';
import { Button, Card, CardHeader, EmptyState, Modal, PageSection, StatusBadge } from '@/ui/components';

type ReconcilePageProps = {
  userEmail?: string | null;
};

type ReconcileFilter = 'all' | ReconcilePreviewRow['status'];

const FILTERS: Array<{ key: ReconcileFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'ready', label: 'Ready' },
  { key: 'review', label: 'Needs review' },
  { key: 'partial', label: 'Possible partial' },
  { key: 'manual', label: 'Manual match' },
  { key: 'unavailable', label: 'Invoice unavailable' },
  { key: 'unmatched', label: 'No candidate' },
  { key: 'excluded', label: 'Excluded' },
  { key: 'paid', label: 'Already paid' },
  { key: 'duplicate', label: 'Duplicate' },
];

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return 'Reconcile request failed.';
}

function formatDate(value: unknown) {
  const date = new Date(String(value || '').trim());
  if (Number.isNaN(date.getTime())) return String(value || 'No date');
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMonth(monthKey: string) {
  const date = new Date(`${monthKey}-01T12:00:00`);
  return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long' });
}

function formatCurrency(value: unknown) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
    : '—';
}

function getAccountScopeLabel(userEmail?: string | null) {
  const email = String(userEmail || '').trim().toLowerCase();
  if (email === 'yl@linfinityeng.com' || email === 'jp@linfinityeng.com') return 'Linfinity invoices';
  if (email === 'wd@linfinityeng.com' || email === 'wd@eqeng.com.au' || email === 'wdu@eqeng.com.au') return 'Equilibrium invoices';
  return 'Authorized invoices';
}

function statusLabel(row: ReconcilePreviewRow, applied: boolean) {
  if (applied) return 'Applied';
  if (row.status === 'ready') return 'Ready';
  if (row.status === 'review') return 'Needs review';
  if (row.status === 'partial') return 'Possible partial';
  if (row.status === 'manual') return 'Manual match';
  if (row.status === 'unavailable') return 'Invoice unavailable';
  if (row.status === 'excluded') return 'Excluded';
  if (row.status === 'paid') return 'Already paid';
  if (row.status === 'duplicate') return 'Duplicate';
  return 'No candidate';
}

function candidateEvidence(reasons: string[]) {
  return reasons.slice(0, 3).join(' · ');
}

function selectedCandidate(row: ReconcilePreviewRow, invoiceId?: number) {
  return row.candidates.find((candidate) => candidate.invoiceId === invoiceId) || null;
}

function reconcileRowKey(row: ReconcilePreviewRow) {
  return `${row.fileHash}:${row.transactionId}`;
}

function defaultInvoiceSelections(preview: ReconcilePreview) {
  const selections = new Map<string, number>();
  preview.rows.forEach((row) => {
    if (['ready', 'review', 'partial', 'paid', 'unavailable'].includes(row.status) && row.candidates[0]) {
      selections.set(reconcileRowKey(row), row.candidates[0].invoiceId);
    }
  });
  return selections;
}

function initialAllocations(result: ReconcilePreview, restore: boolean) {
  const selections = new Map<string, ReconcileAllocation[]>(Object.entries(restore ? result.decisions?.allocationSelections || {} : {}));
  result.rows.forEach((row) => {
    const key = reconcileRowKey(row);
    if (row.suggestedAllocations && row.suggestedAllocations.length > 1 && (!selections.has(key) || row.status === 'duplicate')) {
      selections.set(key, row.suggestedAllocations);
    }
  });
  return selections;
}

export default function ReconcilePage({ userEmail }: ReconcilePageProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const restoreStartedRef = useRef(false);
  const [preview, setPreview] = useState<ReconcilePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [applying, setApplying] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<'error' | 'success' | 'warning'>('success');
  const [filter, setFilter] = useState<ReconcileFilter>('all');
  const [newestFirst, setNewestFirst] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(() => new Set());
  const [invoiceSelections, setInvoiceSelections] = useState<Map<string, number>>(() => new Map());
  const [allocationSelections, setAllocationSelections] = useState<Map<string, ReconcileAllocation[]>>(() => new Map());
  const [appliedRows, setAppliedRows] = useState<Set<string>>(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedBatches, setSavedBatches] = useState<ReconcileBatchSummary[]>([]);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [persistenceAvailable, setPersistenceAvailable] = useState<boolean | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [restoring, setRestoring] = useState(false);

  function hydratePreview(result: ReconcilePreview, restoreDecisions: boolean) {
    const decisions = restoreDecisions ? result.decisions : undefined;
    const selections = decisions
      ? new Map(Object.entries(decisions.invoiceSelections).map(([key, invoiceId]) => [key, Number(invoiceId)]))
      : defaultInvoiceSelections(result);
    result.rows.forEach((row) => {
      const recorded = row.candidates.filter((candidate) => candidate.signals.includes('recorded_payment'));
      if (row.status === 'duplicate' && recorded.length === 1) {
        selections.set(reconcileRowKey(row), recorded[0].invoiceId);
      }
    });
    const applied = new Set(decisions?.appliedTransactionIds || []);
    const allocations = initialAllocations(result, restoreDecisions);
    const applicableKeys = new Set(result.rows
      .filter((row) => !['paid', 'unavailable', 'excluded', 'duplicate'].includes(row.status)
        && !row.duplicate
        && !row.excludedReason
        && !applied.has(row.transactionId)
        && (allocations.has(reconcileRowKey(row))
          ? validAllocations(row, allocations.get(reconcileRowKey(row))!, result)
          : row.candidates.some((candidate) => candidate.invoiceId === selections.get(reconcileRowKey(row))
            && Math.round(row.amount * 100) <= Math.round(candidate.outstandingBalance * 100))))
      .map(reconcileRowKey));
    setPreview(result);
    setActiveBatchId(result.batchId || null);
    setPersistenceAvailable(result.persistenceAvailable ?? false);
    setInvoiceSelections(selections);
    setAllocationSelections(allocations);
    setSelectedRows(new Set((decisions?.selectedRowKeys || []).filter((key) => applicableKeys.has(key))));
    setAppliedRows(applied);
    setFilter('all');
    setSaveState(result.batchId ? 'saved' : 'idle');
    return selections;
  }

  async function refreshSavedBatches() {
    const list = await financeService.listReconciliationBatches();
    setPersistenceAvailable(list.available);
    setSavedBatches(list.batches);
    return list;
  }

  function persistDecisions(
    batchId: string | null,
    nextSelectedRows: Set<string>,
    nextInvoiceSelections: Map<string, number>,
    nextAppliedRows: Set<string>,
    nextAllocations = allocationSelections,
  ) {
    if (!batchId) return;
    const decisions: ReconcileBatchDecisions = {
      selectedRowKeys: Array.from(nextSelectedRows),
      invoiceSelections: Object.fromEntries(nextInvoiceSelections),
      allocationSelections: Object.fromEntries(nextAllocations),
      appliedTransactionIds: Array.from(nextAppliedRows),
    };
    setSaveState('saving');
    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        await financeService.updateReconciliationBatch(batchId, decisions);
        setSaveState('saved');
      })
      .catch(() => {
        setSaveState('error');
      });
  }

  async function openSavedBatch(batchId: string, showFeedback = true) {
    setRestoring(true);
    try {
      const result = await financeService.getReconciliationBatch(batchId);
      hydratePreview(result, true);
      if (showFeedback) {
        setFeedback(`Restored ${result.summary.totalDeposits} deposits. Invoice matches were refreshed against the current ledger.`);
        setFeedbackTone('success');
      }
    } catch (error) {
      setFeedback(normalizeErrorMessage(error));
      setFeedbackTone('error');
      await refreshSavedBatches().catch(() => undefined);
    } finally {
      setRestoring(false);
    }
  }

  useEffect(() => {
    if (restoreStartedRef.current) return;
    restoreStartedRef.current = true;
    void (async () => {
      try {
        const list = await refreshSavedBatches();
        if (list.available && list.batches[0]) await openSavedBatch(list.batches[0].batchId, false);
      } catch {
        setPersistenceAvailable(false);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const rows = (preview?.rows || []).filter((row) => filter === 'all' || row.status === filter);
    return [...rows].sort((left, right) => {
      const direction = newestFirst ? -1 : 1;
      return direction * (left.date.localeCompare(right.date)
        || left.fileName.localeCompare(right.fileName)
        || left.sourceOrder - right.sourceOrder);
    });
  }, [filter, newestFirst, preview?.rows]);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, ReconcilePreviewRow[]>();
    filteredRows.forEach((row) => {
      groups.set(row.monthKey, [...(groups.get(row.monthKey) || []), row]);
    });
    return Array.from(groups.entries());
  }, [filteredRows]);

  const readyRows = useMemo(
    () => (preview?.rows || []).filter((row) => row.status === 'ready' && !appliedRows.has(row.transactionId)),
    [appliedRows, preview?.rows],
  );

  const selectedPaymentRows = useMemo(
    () => (preview?.rows || []).filter((row) => selectedRows.has(reconcileRowKey(row))),
    [preview?.rows, selectedRows],
  );

  const selectedTotal = useMemo(
    () => selectedPaymentRows.reduce((sum, row) => sum + row.amount, 0),
    [selectedPaymentRows],
  );

  const allReadySelected = readyRows.length > 0 && readyRows.every((row) => selectedRows.has(reconcileRowKey(row)));

  async function handleFiles(files: File[]) {
    if (files.length === 0) return;
    setLoading(true);
    setFeedback(null);
    setSelectedRows(new Set());
    setAppliedRows(new Set());
    setFilter('all');
    try {
      const result = await financeService.previewReconciliation(files);
      const selections = hydratePreview(result, false);
      persistDecisions(result.batchId || null, new Set(), selections, new Set(), initialAllocations(result, false));
      if (result.batchId) await refreshSavedBatches().catch(() => undefined);
      const failedFiles = result.files.filter((file) => file.status === 'error').length;
      const transient = result.persistenceAvailable === false ? ' Draft saving is unavailable until migration 093 is applied.' : '';
      setFeedback(`${result.summary.totalDeposits} deposits loaded in Year/Month order.${failedFiles ? ` ${failedFiles} file(s) could not be read.` : ''}${transient}`);
      setFeedbackTone(failedFiles || transient ? 'warning' : 'success');
    } catch (error) {
      setPreview(null);
      setFeedback(normalizeErrorMessage(error));
      setFeedbackTone('error');
    } finally {
      setLoading(false);
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || []);
    if (files.length) void handleFiles(files);
    event.target.value = '';
  }

  function handleDragEnter(event: React.DragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    if (loading) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = loading ? 'none' : 'copy';
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
    if (!isDraggingFiles) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFiles(false);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (loading) return;
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) void handleFiles(files);
  }

  function setInvoiceForRow(row: ReconcilePreviewRow, value: string) {
    const invoiceId = Number(value);
    const key = reconcileRowKey(row);
    const nextSelections = new Map(invoiceSelections);
    if (Number.isInteger(invoiceId) && invoiceId > 0) nextSelections.set(key, invoiceId);
    else nextSelections.delete(key);
    const nextSelectedRows = new Set(selectedRows);
    nextSelectedRows.delete(key);
    setInvoiceSelections(nextSelections);
    setSelectedRows(nextSelectedRows);
    persistDecisions(activeBatchId, nextSelectedRows, nextSelections, appliedRows);
  }

  function rowCanApply(row: ReconcilePreviewRow) {
    return !['paid', 'unavailable', 'excluded', 'duplicate'].includes(row.status)
      && !row.duplicate
      && !row.excludedReason
      && !appliedRows.has(row.transactionId)
      && (allocationSelections.has(reconcileRowKey(row))
        ? validAllocations(row, allocationSelections.get(reconcileRowKey(row))!, preview)
        : Boolean(selectedCandidate(row, invoiceSelections.get(reconcileRowKey(row)))
          && Math.round(row.amount * 100) <= Math.round(selectedCandidate(row, invoiceSelections.get(reconcileRowKey(row)))!.outstandingBalance * 100)));
  }

  function allocationOptions(row: ReconcilePreviewRow, result = preview) {
    return Array.from(new Map([...(result?.allocationInvoices || []), ...row.candidates].map((invoice) => [invoice.invoiceId, invoice])).values());
  }

  function validAllocations(row: ReconcilePreviewRow, allocations: ReconcileAllocation[], result: ReconcilePreview | null) {
    return allocations.length > 0 && allocations.length <= 20
      && new Set(allocations.map((item) => item.invoiceId)).size === allocations.length
      && allocations.every((item) => {
        const invoice = allocationOptions(row, result).find((candidate) => candidate.invoiceId === item.invoiceId);
        return invoice && item.amountReceived > 0 && Math.abs(item.amountReceived * 100 - Math.round(item.amountReceived * 100)) < 0.00001
          && Math.round(item.amountReceived * 100) <= Math.round(invoice.outstandingBalance * 100);
      })
      && allocations.reduce((total, item) => total + Math.round(item.amountReceived * 100), 0) === Math.round(row.amount * 100);
  }

  function editAllocations(row: ReconcilePreviewRow, allocations: ReconcileAllocation[] | null) {
    const key = reconcileRowKey(row);
    const next = new Map(allocationSelections);
    if (allocations) next.set(key, allocations); else next.delete(key);
    const selected = new Set(selectedRows);
    selected.delete(key);
    setAllocationSelections(next);
    setSelectedRows(selected);
    persistDecisions(activeBatchId, selected, invoiceSelections, appliedRows, next);
  }

  function toggleRow(row: ReconcilePreviewRow) {
    if (!rowCanApply(row)) return;
    const key = reconcileRowKey(row);
    const next = new Set(selectedRows);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedRows(next);
    persistDecisions(activeBatchId, next, invoiceSelections, appliedRows);
  }

  function toggleAllReady() {
    const next = new Set(selectedRows);
    if (allReadySelected) readyRows.forEach((row) => next.delete(reconcileRowKey(row)));
    else readyRows.filter(rowCanApply).forEach((row) => next.add(reconcileRowKey(row)));
    setSelectedRows(next);
    persistDecisions(activeBatchId, next, invoiceSelections, appliedRows);
  }

  async function applySelectedRows() {
    if (selectedPaymentRows.length === 0 || selectedPaymentRows.some((row) => !rowCanApply(row))) return;
    setApplying(true);
    try {
      const result = await financeService.applyReconciliation(selectedPaymentRows.map((row) => ({
        transactionId: row.transactionId,
        invoiceId: invoiceSelections.get(reconcileRowKey(row)) as number,
        allocations: allocationSelections.get(reconcileRowKey(row)),
        paidAt: row.date,
        amountReceived: row.amount,
        fileName: row.fileName,
        page: row.page,
        description: row.description,
      })), activeBatchId);
      const completed = new Set(result.results.map((item) => item.transactionId));
      const nextAppliedRows = new Set([...appliedRows, ...completed]);
      const nextSelectedRows = new Set<string>();
      setAppliedRows(nextAppliedRows);
      setSelectedRows(nextSelectedRows);
      persistDecisions(activeBatchId, nextSelectedRows, invoiceSelections, nextAppliedRows);
      setConfirmOpen(false);
      const paidInFull = result.invoices.filter((invoice) => invoice.paidInFull).length;
      setFeedback(`${result.appliedCount} payment${result.appliedCount === 1 ? '' : 's'} applied.${paidInFull ? ` ${paidInFull} invoice${paidInFull === 1 ? '' : 's'} marked paid.` : ''}${result.duplicateCount ? ` ${result.duplicateCount} duplicate${result.duplicateCount === 1 ? ' was' : 's were'} skipped.` : ''}`);
      setFeedbackTone('success');
    } catch (error) {
      setConfirmOpen(false);
      setFeedback(normalizeErrorMessage(error));
      setFeedbackTone('error');
    } finally {
      setApplying(false);
    }
  }

  async function deleteActiveBatch() {
    if (!activeBatchId || !window.confirm('Delete this saved reconciliation draft? The original PDFs are not stored.')) return;
    setRestoring(true);
    try {
      await financeService.deleteReconciliationBatch(activeBatchId);
      setPreview(null);
      setActiveBatchId(null);
      setSelectedRows(new Set());
      setInvoiceSelections(new Map());
      setAppliedRows(new Set());
      setSaveState('idle');
      const list = await refreshSavedBatches();
      if (list.batches[0]) await openSavedBatch(list.batches[0].batchId, false);
      setFeedback('Saved reconciliation draft deleted.');
      setFeedbackTone('success');
    } catch (error) {
      setFeedback(normalizeErrorMessage(error));
      setFeedbackTone('error');
    } finally {
      setRestoring(false);
    }
  }

  function savedBatchLabel(batch: ReconcileBatchSummary) {
    const files = batch.fileNames.length > 0 ? batch.fileNames.join(', ') : 'Untitled batch';
    return `${files} · ${batch.transactionCount} deposits · ${formatDate(batch.updatedAt)}`;
  }

  return (
    <PageSection className="company-ai-reconcile" width="wide" padding="compact">
      <div className="company-ai-reconcile-grid">
        <Card
          className="company-ai-reconcile-upload"
          padding="lg"
          header={(
            <CardHeader
              title="Reconcile"
              subtitle={`${getAccountScopeLabel(userEmail)} payment ledger`}
              actions={<StatusBadge status="Review required">Review required</StatusBadge>}
            />
          )}
        >
          <div
            className={`company-ai-reconcile-dropzone${isDraggingFiles ? ' is-dragging' : ''}`}
            aria-label="Upload ANZ transaction report PDFs"
            aria-busy={loading}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="company-ai-reconcile-dropzone__icon"><FileText size={20} /></div>
            <div className="company-ai-reconcile-dropzone__body">
              <div className="company-ai-reconcile-title">
                {isDraggingFiles ? 'Drop PDFs to upload' : 'ANZ reports and bank statements'}
              </div>
              <div className="company-ai-muted">
                {isDraggingFiles
                  ? 'Release to load and match the selected reports.'
                  : 'Drag and drop one or more native-text PDFs, or choose files. Scanned documents are not supported.'}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              className="company-ai-reconcile-file-input"
              onChange={handleInputChange}
            />
            <Button
              type="button"
              variant="primary"
              loading={loading}
              iconBefore={<Upload size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload PDFs
            </Button>
          </div>

          <div className="company-ai-reconcile-privacy">
            <strong>Privacy:</strong> PDFs are parsed in memory and are not stored. PMApp saves only filenames, normalized transaction details, match results, and your review choices until the draft expires.
          </div>

          {persistenceAvailable !== false ? (
            <div className="company-ai-reconcile-drafts" aria-label="Saved reconciliation drafts">
              <label>
                <span>Saved batch</span>
                <select
                  value={activeBatchId || ''}
                  disabled={restoring || savedBatches.length === 0}
                  onChange={(event) => {
                    if (event.target.value) void openSavedBatch(event.target.value);
                  }}
                >
                  {savedBatches.length === 0 ? <option value="">No saved batches yet</option> : null}
                  {savedBatches.map((batch) => (
                    <option key={batch.batchId} value={batch.batchId}>{savedBatchLabel(batch)}</option>
                  ))}
                </select>
              </label>
              <span className={`company-ai-reconcile-save-state company-ai-reconcile-save-state--${saveState}`}>
                {restoring ? 'Refreshing…' : saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Save failed' : activeBatchId ? 'Saved' : 'Autosave ready'}
              </span>
              <Button
                type="button"
                variant="secondary"
                iconBefore={<Trash2 size={15} />}
                disabled={!activeBatchId || restoring}
                onClick={() => void deleteActiveBatch()}
              >
                Delete draft
              </Button>
            </div>
          ) : (
            <div className="company-ai-reconcile-privacy company-ai-reconcile-privacy--warning">
              Draft persistence is not available yet. Upload and apply still work, but leaving this page will clear the review.
            </div>
          )}

          {preview?.files.length ? (
            <div className="company-ai-reconcile-files">
              {preview.files.map((file, index) => (
                <div key={`${file.fileName}-${index}`} className={`company-ai-reconcile-file company-ai-reconcile-file--${file.status}`}>
                  <span><FileText size={15} /></span>
                  <span>
                    <strong>{file.fileName}</strong>
                    <small>
                      {file.status === 'parsed'
                        ? `${formatDate(file.reportStart)} to ${formatDate(file.reportEnd)} · ${file.depositCount} deposits · ${formatCurrency(file.extractedDepositTotal)}`
                        : file.error}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {feedback ? (
            <div className={`company-ai-feedback company-ai-feedback--${feedbackTone}`}>
              {feedbackTone === 'error' || feedbackTone === 'warning' ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
              <span>{feedback}</span>
            </div>
          ) : null}
        </Card>

        <Card padding="lg">
          <div className="company-ai-inline-metrics">
            <div className="company-ai-inline-metric"><strong>{preview?.summary.totalDeposits || 0}</strong><span>Deposits</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.ready || 0}</strong><span>Ready</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.review || 0}</strong><span>Review</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.partial || 0}</strong><span>Possible partial</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.manual || 0}</strong><span>Manual match</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.unavailable || 0}</strong><span>Unavailable</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.unmatched || 0}</strong><span>No candidate</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.paid || 0}</strong><span>Already paid</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.excluded || 0}</strong><span>Excluded</span></div>
            <div className="company-ai-inline-metric"><strong>{preview?.summary.duplicate || 0}</strong><span>Duplicates</span></div>
          </div>
          <div className="company-ai-reconcile-total">
            <span>Uploaded deposits</span>
            <strong>{formatCurrency(preview?.summary.totalAmount || 0)}</strong>
          </div>
          <div className="company-ai-reconcile-actions">
            <Button
              type="button"
              variant="secondary"
              iconBefore={<RefreshCw size={16} />}
              disabled={loading}
              onClick={() => fileInputRef.current?.click()}
            >
              Replace PDFs
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={selectedRows.size === 0}
              onClick={() => setConfirmOpen(true)}
            >
              Apply selected ({selectedRows.size})
            </Button>
          </div>
        </Card>
      </div>

      {preview?.rows.length ? (
        <Card padding="none" className="company-ai-reconcile-review-card">
          <div className="company-ai-reconcile-toolbar">
            <div className="company-ai-reconcile-filters" aria-label="Reconcile filters">
              {FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={filter === item.key ? 'is-active' : ''}
                  onClick={() => setFilter(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="company-ai-reconcile-toolbar__actions">
              <label className="company-ai-reconcile-select-ready">
                <input type="checkbox" checked={allReadySelected} onChange={toggleAllReady} />
                Select all ready
              </label>
              <button type="button" className="company-ai-reconcile-sort" onClick={() => setNewestFirst((value) => !value)}>
                {newestFirst ? 'Newest first' : 'Oldest first'}
              </button>
            </div>
          </div>

          <div className="company-ai-reconcile-table" role="table" aria-label="Deposit reconciliation review">
            <div className="company-ai-reconcile-table__head" role="row">
              <span>Select</span>
              <span>Deposit</span>
              <span>Bank details</span>
              <span>Suggested invoice</span>
              <span>Invoice amounts</span>
              <span>Match evidence</span>
            </div>
            {groupedRows.map(([monthKey, rows]) => (
              <div key={monthKey} className="company-ai-reconcile-month">
                <div className="company-ai-reconcile-month__title">{formatMonth(monthKey)}</div>
                {rows.map((row) => {
                  const selectedInvoiceId = invoiceSelections.get(reconcileRowKey(row));
                  const candidate = selectedCandidate(row, selectedInvoiceId);
                  const allocations = allocationSelections.get(reconcileRowKey(row));
                  const evidenceCandidate = candidate || row.candidates[0] || null;
                  const applied = appliedRows.has(row.transactionId);
                  const locked = ['paid', 'unavailable', 'excluded', 'duplicate'].includes(row.status) || row.duplicate || Boolean(row.excludedReason) || applied;
                  const canApply = rowCanApply(row);
                  const explanatoryText = row.duplicateReason
                    || row.excludedReason
                    || row.unavailableReason
                    || (evidenceCandidate ? candidateEvidence(evidenceCandidate.reasons) : 'No useful invoice candidate found.');
                  return (
                    <div key={`${row.transactionId}-${row.fileHash}`} className={`company-ai-reconcile-table__row company-ai-reconcile-table__row--${row.status}`} role="row">
                      <span className="company-ai-reconcile-table__check" role="cell">
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.description}`}
                          checked={selectedRows.has(reconcileRowKey(row))}
                          disabled={!canApply}
                          onChange={() => toggleRow(row)}
                        />
                      </span>
                      <span role="cell" data-label="Deposit">
                        <strong>{formatCurrency(row.amount)}</strong>
                        <small>{formatDate(row.date)}</small>
                      </span>
                      <span role="cell" data-label="Bank details">
                        <strong>{row.description}</strong>
                        <small>{row.fileName} · page {row.page}</small>
                      </span>
                      <span role="cell" data-label="Suggested invoice">
                        {allocations ? (
                          <div className="company-ai-reconcile-allocations" aria-label={`Allocations for ${row.description}`}>
                            {allocations.map((allocation, index) => (
                              <div className="company-ai-reconcile-allocation" key={index}>
                                <select aria-label={`Allocation ${index + 1} invoice for ${row.description}`} value={allocation.invoiceId || ''} disabled={locked}
                                  onChange={(event) => editAllocations(row, allocations.map((item, at) => at === index ? { ...item, invoiceId: Number(event.target.value) } : item))}>
                                  <option value="">Choose invoice</option>
                                  {allocationOptions(row).map((invoice) => <option key={invoice.invoiceId} value={invoice.invoiceId}>{invoice.invoiceNumber} · {invoice.client} · remaining {formatCurrency(invoice.outstandingBalance)}</option>)}
                                </select>
                                <label><small>Amount</small><input type="number" min="0" step="0.01" aria-label={`Allocation ${index + 1} amount for ${row.description}`} value={allocation.amountReceived} disabled={locked}
                                  onChange={(event) => editAllocations(row, allocations.map((item, at) => at === index ? { ...item, amountReceived: Number(event.target.value) } : item))} /></label>
                                {!locked && <Button variant="secondary" onClick={() => editAllocations(row, allocations.filter((_, at) => at !== index))}>Remove allocation {index + 1}</Button>}
                              </div>
                            ))}
                            <small>Allocated {formatCurrency(allocations.reduce((total, item) => total + item.amountReceived, 0))} of {formatCurrency(row.amount)}</small>
                            {!locked && !validAllocations(row, allocations, preview) && <small role="alert">Choose different invoices with sufficient balances and amounts totalling this deposit.</small>}
                            {!locked && allocations.length < 20 && <Button variant="secondary" onClick={() => editAllocations(row, [...allocations, { invoiceId: 0, amountReceived: 0 }])}>Add invoice</Button>}
                          </div>
                        ) : <>
                        <select
                          aria-label={`Invoice for ${row.description}`}
                          value={selectedInvoiceId || ''}
                          disabled={['paid', 'unavailable', 'excluded', 'duplicate'].includes(row.status) || row.duplicate || Boolean(row.excludedReason) || applied}
                          onChange={(event) => setInvoiceForRow(row, event.target.value)}
                        >
                          <option value="">No invoice selected</option>
                          {row.candidates.map((option) => (
                            <option key={option.invoiceId} value={option.invoiceId}>
                              {option.invoiceNumber} · {option.client} · {option.score} pts
                              {option.rfiTicketNumber ? ` · RFI ${option.rfiTicketNumber}` : ''}
                              {option.projectAddress ? ` · ${option.projectAddress}` : ''}
                            </option>
                          ))}
                        </select>
                        <small>
                          {candidate?.client
                            || (row.candidates.length
                              ? `${row.candidates.length} candidate${row.candidates.length === 1 ? '' : 's'} available for manual choice.`
                              : row.excludedReason ? 'Not available for invoice matching.' : explanatoryText)}
                        </small>
                        {candidate?.rfiTicketNumber && <small>RFI {candidate.rfiTicketNumber}</small>}
                        {candidate?.projectAddress && <small>{candidate.projectAddress}</small>}
                        </>}
                        {!locked && <Button variant="secondary" onClick={() => editAllocations(row, allocations ? null : [
                          { invoiceId: selectedInvoiceId || 0, amountReceived: row.amount }, { invoiceId: 0, amountReceived: 0 },
                        ])}>{allocations ? 'Use one invoice' : 'Split across invoices'}</Button>}
                      </span>
                      <span role="cell" data-label="Invoice amounts">
                        {allocations ? allocations.map((allocation, index) => {
                          const invoice = allocationOptions(row).find((item) => item.invoiceId === allocation.invoiceId);
                          return <div className="company-ai-reconcile-allocation-balance" key={index}><strong>{invoice?.invoiceNumber || 'Choose invoice'} · {formatCurrency(allocation.amountReceived)}</strong><small>Remaining {formatCurrency(invoice?.outstandingBalance || 0)}</small></div>;
                        }) : candidate ? (
                          <>
                            <strong>Total {formatCurrency(candidate.originalTotal)}</strong>
                            <small>Remaining {formatCurrency(candidate.outstandingBalance)}</small>
                          </>
                        ) : <span className="company-ai-muted">—</span>}
                      </span>
                      <span role="cell" data-label="Match evidence">
                        <span className={`company-ai-reconcile-status company-ai-reconcile-status--${applied ? 'applied' : row.status}`}>
                          {statusLabel(row, applied)}
                        </span>
                        <small>{evidenceCandidate ? `${evidenceCandidate.score} pts · ${explanatoryText}` : explanatoryText}</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <div className="company-ai-reconcile-results">
          <EmptyState
            icon={<FileText size={28} />}
            title="No bank deposits loaded"
            description="Upload one or more native-text ANZ transaction report or monthly bank statement PDFs to start reconciliation."
          />
        </div>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Apply selected deposits"
        size="md"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={applying} onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button type="button" variant="primary" loading={applying} onClick={() => void applySelectedRows()}>
              Apply {selectedRows.size} payment{selectedRows.size === 1 ? '' : 's'}
            </Button>
          </>
        )}
      >
        <div className="company-ai-reconcile-confirm">
          <p>Apply {selectedRows.size} selected bank deposit{selectedRows.size === 1 ? '' : 's'} totalling <strong>{formatCurrency(selectedTotal)}</strong>?</p>
          <p className="company-ai-muted">PMApp will record them in the payment ledger and mark fully covered invoices as paid.</p>
          <ul>
            {selectedPaymentRows.map((row) => {
              const candidate = selectedCandidate(row, invoiceSelections.get(reconcileRowKey(row)));
              const allocations = allocationSelections.get(reconcileRowKey(row));
              return (
                <li key={reconcileRowKey(row)}>
                  {formatDate(row.date)} · {formatCurrency(row.amount)}
                  {allocations ? allocations.map((allocation) => {
                    const invoice = allocationOptions(row).find((item) => item.invoiceId === allocation.invoiceId);
                    return <div key={allocation.invoiceId}>{formatCurrency(allocation.amountReceived)} → <strong>{invoice?.invoiceNumber}</strong> · {invoice?.client}</div>;
                  }) : <> → <strong>{candidate?.invoiceNumber}</strong> · {candidate?.client}</>}
                  <div className="company-ai-muted">{row.description}</div>
                  {candidate?.signals.some((signal) => ['exceeds_balance', 'batch_exceeds_balance'].includes(signal)) ? (
                    <div className="company-ai-muted">{candidate.reasons.find((reason) => reason.includes('exceed'))}</div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>
    </PageSection>
  );
}
